import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SUPPORTED_LANGUAGES, type LanguageCode } from "@/i18n/config";
import { prefetchLanguage, ensureLanguageLoaded } from "@/i18n/config";
import { FlagIcon } from "@/components/public/FlagIcon";

interface LanguageSwitcherProps {
  variant?: "nav" | "compact";
}

export const LanguageSwitcher = ({ variant = "nav" }: LanguageSwitcherProps) => {
  const { i18n, t } = useTranslation();
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t("common.language")}
        className={
          variant === "compact"
            ? "inline-flex items-center justify-center h-9 w-9 rounded-lg border hairline-2 text-foreground hover:bg-secondary/60 transition-colors focusable"
            : "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[13px] font-medium text-foreground-muted hover:bg-secondary/60 hover:text-foreground transition-colors focusable"
        }
      >
        {variant === "compact" ? (
          <Globe className="h-4 w-4" />
        ) : (
          <>
            <FlagIcon code={current.code as LanguageCode} />
            <span>{current.code.toUpperCase()}</span>
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px] z-50 bg-background">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onPointerEnter={() => prefetchLanguage(lang.code)}
            onFocus={() => prefetchLanguage(lang.code)}
            onClick={() => {
              try { localStorage.setItem("tirevault_language_manual", "1"); } catch { /* ignore */ }
              // Ladda språkbundle först, byt sedan — annars krävs två klick på
              // mobiler där bundle inte hunnit prefetchas via hover/focus.
              void ensureLanguageLoaded(lang.code).then(() => i18n.changeLanguage(lang.code));
            }}
            className="flex items-center justify-between gap-3 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <FlagIcon code={lang.code as LanguageCode} />
              <span>{lang.label}</span>
            </span>
            {i18n.language === lang.code && <Check className="h-4 w-4 text-accent" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
